import { config } from "@/config/env";
import { type UserRole, type GetUsersResponse, type SetUserRoleResponse, type DeleteUserRoleResponse, type AddQuotaRequest, type AddQuotaResponse } from "@/types/access";

// Helper function to get the current auth token (e.g. from Clerk)
// We need to pass the token from the component/hook level or use a closure if we were inside a hook.
// Since this is a standalone utility, we'll design it to accept the token, or we can use the `window.Clerk` object if available,
// but passing the token is cleaner and more testable.
// However, to make it easier to use in hooks, we can create a class or just functions that take the token.

const API_BASE_URL = config.apiUrl;

async function fetchWithAuth(path: string, token: string, options: RequestInit = {}) {
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
    ...options.headers,
  };
  
  const response = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API Error: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
}

export const accessApi = {
  getUsers: (token: string, page = 1, limit = 10) => 
    fetchWithAuth(`/admin/access/users/list?page=${page}&limit=${limit}`, token).then(res => res as GetUsersResponse),

  listUserRoles: (token: string, role?: UserRole) => {
    const query = role ? `?role=${role}` : "";
    return fetchWithAuth(`/admin/access/users${query}`, token);
  },

  setUserRole: (token: string, userId: string, role: UserRole) =>
    fetchWithAuth(`/admin/access/users/${userId}/role`, token, {
      method: "POST",
      body: JSON.stringify({ role }),
    }).then(res => res as SetUserRoleResponse),

  deleteUserRole: (token: string, userId: string) =>
    fetchWithAuth(`/admin/access/users/${userId}/role`, token, {
      method: "DELETE",
    }).then(res => res as DeleteUserRoleResponse),

  addQuota: (token: string, userId: string, amount: number) =>
    fetchWithAuth(`/admin/quota/add`, token, {
      method: "POST",
      body: JSON.stringify({ userId, amount }),
    }).then(res => res as AddQuotaResponse),
};
